import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { MEDIA } from "@/constants/media";
import { scrollToSection } from "@/lib/scroll";
import { EASE } from "./Reveal";

const LINES = ["A whole new", "way to shop."];

export default function Hero() {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section ref={ref} id="top" className="relative h-[100svh] min-h-[620px] w-full overflow-hidden bg-white">
            <motion.div style={reduce ? undefined : { y: imgY }} className="absolute inset-0">
                <motion.img
                    src={MEDIA.hero.src}
                    alt={MEDIA.hero.alt}
                    initial={reduce ? { scale: 1 } : { scale: 1.18 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 2.8, ease: EASE }}
                    className="h-full w-full object-cover object-top"
                    fetchpriority="high"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/55 to-transparent" />
            <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/70 to-transparent" />

            <motion.div
                style={reduce ? undefined : { y: contentY, opacity: fade }}
                className="absolute inset-x-0 bottom-0 z-10 px-6 pb-12 sm:px-10 sm:pb-16 lg:px-16"
            >
                <div className="mx-auto max-w-[1320px]">
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 0.3, ease: EASE }}
                        className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-slate-950/10 bg-white/60 px-4 py-2 text-xs font-light uppercase tracking-[0.3em] text-slate-600 backdrop-blur-md"
                        data-testid="hero-coming-soon-chip"
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-950" />
                        Coming soon
                    </motion.p>
                    <h1 className="text-5xl font-extralight leading-[1.02] tracking-[-0.03em] text-slate-950 sm:text-7xl lg:text-8xl">
                        {LINES.map((line, i) => (
                            <span key={line} className="block overflow-hidden pb-1">
                                <motion.span
                                    className="block"
                                    initial={{ y: "112%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 1.2, delay: 0.45 + i * 0.14, ease: EASE }}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.85, ease: EASE }}
                        className="mt-7 max-w-md text-base font-extralight leading-relaxed text-slate-600 sm:text-lg"
                        data-testid="hero-support-text"
                    >
                        Discover what you love. Find what you're looking for. Shop smarter.
                    </motion.p>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <button
                            onClick={() => scrollToSection("about")}
                            data-testid="hero-explore-btn"
                            className="rounded-full bg-slate-950 px-8 py-3.5 text-sm font-light tracking-wide text-white transition-colors duration-300 hover:bg-slate-800 active:scale-95"
                        >
                            Explore EDIT
                        </button>
                        <button
                            onClick={() => scrollToSection("waitlist")}
                            data-testid="hero-waitlist-btn"
                            className="rounded-full border border-slate-950/15 bg-white/60 px-8 py-3.5 text-sm font-light tracking-wide text-slate-950 backdrop-blur-md transition-colors duration-300 hover:bg-white active:scale-95"
                        >
                            Join the Waitlist
                        </button>
                    </motion.div>
                </div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-12 right-6 z-10 hidden flex-col items-center gap-3 sm:right-10 lg:right-16 md:flex"
                aria-hidden="true"
            >
                <span className="text-[10px] font-light uppercase tracking-[0.35em] text-slate-400 [writing-mode:vertical-rl]">
                    Scroll
                </span>
                <span className="relative h-14 w-px overflow-hidden bg-slate-200">
                    <motion.span
                        className="absolute left-0 top-0 h-5 w-px bg-slate-950"
                        animate={reduce ? undefined : { y: [-20, 56] }}
                        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    />
                </span>
            </motion.div>
        </section>
    );
}
